import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { PrimaryButton } from './ui/primary-button';
import { SecondaryButton } from './ui/secondary-button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { AlertCircle, Lock } from 'lucide-react';

interface VaultPasswordDialogProps {
  isOpen: boolean;
  vaultName: string;
  vaultId: string;
  onClose: () => void;
  onSuccess: (vaultId: string, password: string) => void;
  isLoading?: boolean;
  error?: string;
}

/**
 * Dialog component for entering vault password
 * Prompts user for password to unlock and load a vault
 */
export const VaultPasswordDialog: React.FC<VaultPasswordDialogProps> = ({
  isOpen,
  vaultName,
  vaultId,
  onClose,
  onSuccess,
  isLoading = false,
  error
}) => {
  const [password, setPassword] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password.trim()) {
      onSuccess(vaultId, password);
    }
  };

  const handleClose = () => {
    setPassword('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" onOpenAutoFocus={(e) => e.preventDefault()} onInteractOutside={ e => e.preventDefault() }>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" />
            Unlock Vault
          </DialogTitle>
          <DialogDescription>
            Enter the password for "{vaultName}" to access your secure data.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter vault password"
              disabled={isLoading}
              autoFocus
            />
          </div>
          
          {error && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              <span>{error}</span>
            </div>
          )}
          
          <div className="flex justify-end gap-2">
            <SecondaryButton
              type="button"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              type="submit"
              disabled={!password.trim() || isLoading}
            >
              {isLoading ? 'Unlocking...' : 'Unlock Vault'}
            </PrimaryButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default VaultPasswordDialog;